import Button, { FILLED_BUTTON_TYPE_DARK, OUTLINED_BUTTON_TYPE_DARK } from 'components/Button/Button';
import CheckIcon from 'components/icons/CheckIcon';
import CloseIcon from 'components/icons/CloseIcon';
import { DialogueContext } from 'customHooks/useDialogue/useDialogue';
import { JoinUsContext } from 'customHooks/useJoinUs/useJoinUs';
import useLocalised from 'customHooks/useLocalised/useLocalised';
import { JOIN_US_DIALOG_STEP_2 } from 'data/constants';
import { compare_membership, compare_membership_description } from 'data/translationKeys';
import Image from 'next/future/image';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CurrencyDisplay from 'services/CurrencyDisplay/CurrencyDisplay';
import MembershipService from 'services/MembershipService/MembershipService';
import { currencyHandler } from 'utils/membership';
import { CurrencyType, MembershipIdType, MembershipFeatureType, PriceType, MembershipType } from 'types/types';
interface ICompareMembership {
  memberships: MembershipType[];
  features: MembershipFeatureType[];
  currencies: CurrencyType[];
}

export const CompareMembership: FC<ICompareMembership> = ({
  memberships,
  features,
  currencies,
}) => {
  const { joinUsState: state, setState } = useContext(JoinUsContext);

  const compare_membership_text = useLocalised(compare_membership);
  const compare_membership_description_text = useLocalised(
    compare_membership_description
  );

  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      return;
    }
    const setDefaultValues = async () => {
      const defaultCurrency = await MembershipService.getDefaultCurrency(
        currencies
      );
      
      setState(state => ({
        ...state,
        currency: defaultCurrency.code
      }));
    };
    setDefaultValues();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCurrency: CurrencyType | undefined = useMemo(() => {
    const selectedCurrencyCode = state.currency;
    return currencies.find(({ code }) => code === selectedCurrencyCode);
  }, [currencies, state.currency]);

  const { openDialogue } = useContext(DialogueContext);

  const handleSelectMembership = useCallback(
    (membershipId: MembershipIdType) => {
      const membership = memberships.find(({ id }) => id === membershipId);

      const price = membership?.prices.find(
        ({ code }) => code === state.currency
      ) as PriceType;

      setState(state => ({
        ...state,
        membershipId,
        price,
      }));
      openDialogue(JOIN_US_DIALOG_STEP_2);
    },
    [memberships, setState, openDialogue, state.currency]
  );

  const getFormattedPrice = (prices: PriceType[]) => {
    return CurrencyDisplay({
      currency: state?.currency || 'GBP',
      value: Number(
        prices.find(price => price.code === (state?.currency || 'GBP'))?.value.replace(',', '')
      ),
    });
  }

  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  const handleMouseEnter = (columnIndex: number) => {
    setHoveredColumn(columnIndex);
  }
  const handleMouseLeave = () => {
    setHoveredColumn(null);
  }
  const isColumnHighlighted = (columnIndex: number) => {
    return hoveredColumn === columnIndex;
  }

  const tableMembershipHeadColumns = memberships.map((membership, index) => {
    const { image, name, description, prices } = membership;

    const price = getFormattedPrice(prices);
    const colContainerClassName = `col-container ${isColumnHighlighted(index) ? 'col-highlight' : ''}`;
    const colKey = `${index}_col_membership_head`;

    return (
      <th key={colKey}
        scope="col"
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={handleMouseLeave}
      >
        <div className={colContainerClassName}>
          <Image 
            src={`${image}`}
            alt={'alt'}
            width={640}
            height={436}
          />
          <div className="membership-plans-title">{name}</div>
          <div className="membership-plans-price">
            <span>{price}</span>
            <span className="membership-plans-price-duration">per year</span>
          </div>
          <div className="membership-plans-description">
            {description}
          </div>
        </div>
      </th>
    );
  });

  const tableMembershipFeatureRows = features.map((feature, rowIndex) => {
    const { title: featureTitle } = feature;
    let { description: featureDescription } = feature;

    const rowKey = `${rowIndex}_row_feature`;

    featureDescription = featureDescription.includes('::Currency::') ? currencyHandler(featureDescription, selectedCurrency?.code) : featureDescription;

    return (
      <tr key={rowKey}>
        <th 
          scope="row"
          className="membership-feature-row"
        >
          <div className="row-container">
              <h3 className="membership-feature-row-title">{featureTitle}</h3>
              {featureDescription}
          </div>
        </th>
        {memberships.map((membership, colIndex) => {
          const { id: membershipId } = membership;
          const { memberships: featureMemberships } = feature;

          const hasMembershipFeature = featureMemberships.find(item => item.plan === membershipId);

          const colContainerClassName = `col-container ${isColumnHighlighted(colIndex) ? 'col-highlight' : ''}`;
          const cellTitleClassName = `membership-feature-cell-title ${!hasMembershipFeature && 'color-inactive'}`;
          const cellSumaryClassName = `membership-feature-cell-sumary ${!hasMembershipFeature && 'color-inactive'}`;

          const cellFeatureContent = !hasMembershipFeature
            ? <CloseIcon className="icon-no-has-feature" /> : hasMembershipFeature.text !== ''
            ? hasMembershipFeature.text : <CheckIcon />;
          
          const colKey = `${colIndex}_col_membership`;

          return (
            <td 
              key={colKey}
              scope="col"
              className="membership-feature-row"
              onMouseEnter={() => handleMouseEnter(colIndex)}
              onMouseLeave={handleMouseLeave}
            >
              <div className={colContainerClassName}>
                <div className="row-container">
                  <div className="membership-feature-cell">
                    <span className={cellTitleClassName}>{featureTitle}</span>
                    <span className="has-feature">
                      {cellFeatureContent}
                    </span>
                  </div>
                  <div className={cellSumaryClassName}>
                    {featureDescription}
                  </div>
                </div>
              </div>
            </td>
        )})}
      </tr>
    );
  });

  const tableMembershipButtonColumns = memberships.map((membership, index) => {
    const { button_background: buttonBackground, button_color: buttonColor, id: membershipId } = membership;

    const handleButtonClick = () => {
      handleSelectMembership(membershipId);
    }

    const colContainerClassName = `col-container ${isColumnHighlighted(index) ? 'col-highlight' : ''}`;
    
    const buttonHtmlAttributes = {
      style: { backgroundColor: buttonBackground, color: buttonColor }
    }

    const colKey = `${index}_col_membership_button`;

    return (
      <td
        key={colKey}
        scope="col"
        className="membership-button-cell"
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={handleMouseLeave}
      >
        <div className={colContainerClassName}>
          <Button
            title="Become a member"
            onClick={handleButtonClick}
            className="membership-button-become"
            buttonType={FILLED_BUTTON_TYPE_DARK}
            htmlAttributes={buttonHtmlAttributes}
          />
        </div>
      </td>
    );
  });

  return (
    <>
      <div className="compare-membership-background">
        <div className="compare_membership">
          <h1 className="compare_membership__title">
            {compare_membership_text}
          </h1>
          <div className="compare_membership__top_text">
            {compare_membership_description_text}
          </div>
          
          <div className="membership-plans-container">
            <div className="wrap-table">
              <div>
                <table className="membership-plans">
                    <thead>
                      <tr>
                        <th></th>
                        {tableMembershipHeadColumns}
                      </tr>
                    </thead>
                    <tbody>
                      {tableMembershipFeatureRows}
                    </tbody>
                    <tfoot>
                        <tr>
                          <th></th>
                          {tableMembershipButtonColumns}
                        </tr>
                    </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompareMembership;
